/* ============================================================
   Trasse Families — Analytics.

   What the school run actually is, measured from the runs themselves. A
   household is not a fleet, so there is no efficiency league table and nothing
   to police: the questions a parent actually asks are "did it happen", "how
   long does it really take", and "who drove".

   Two rules this page is built on, both learned the hard way elsewhere:

   1. A MISSING NUMBER IS NOT A ZERO. A run that recorded no distance is shown
      as "not recorded" and excluded from totals — never drawn as a zero-height
      bar, which reads as "the car did not move" (the exact bug found in the
      school console's distance-per-route chart, 2026-08-17).

   2. WHAT NOTHING WRITES, NOTHING SHOWS. Fuel spend and driving quality are
      named here as not-yet, because `familyRefuelEvents` has no writer in the
      tree and there is no scorecard collection at all. Inventing either would
      be the "confident and wrong" failure this product keeps guarding against.
   ============================================================ */
import React, { useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { FAM, DISPLAY_FONT, FAMILY_PAGE_WRAP } from './familyTheme';
import {
  FamilyRun, byWeek, minutesLate, totalsFor, useFamilyRuns,
} from './useFamilyRuns';
import { confirmedSpend, needsParent, useFamilyRefuels } from './useFamilyRefuels';

interface Props { orgCode: string; busId: string }

/** Windows a parent actually thinks in. */
const WINDOWS = [
  { key: '4w', label: 'Last 4 weeks', days: 28 },
  { key: '12w', label: 'This term', days: 91 },
  { key: 'all', label: 'Everything', days: 0 },
] as const;
type WindowKey = typeof WINDOWS[number]['key'];

/** Inside this many minutes of the planned time counts as "on time". Wide on
 *  purpose: a school run is traffic, not a train, and a three-minute bar would
 *  make an ordinary household look chronically late. */
const ON_TIME_GRACE_MIN = 10;

const Panel: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <section style={{
    background: '#fff', border: `1px solid ${FAM.line}`, borderRadius: 20,
    padding: 'clamp(18px,3vw,26px)', ...style,
  }}>
    {children}
  </section>
);

const Stat: React.FC<{ value: string; label: string; sub?: string }> = ({ value, label, sub }) => (
  <div style={{ padding: '14px 16px', borderRadius: 14, background: FAM.tint, border: `1px solid ${FAM.tint2}`, minWidth: 0 }}>
    <div style={{
      fontSize: 27, fontWeight: 800, color: FAM.ink, letterSpacing: '-.02em',
      fontVariantNumeric: 'tabular-nums', lineHeight: 1.15,
    }}>{value}</div>
    <div style={{ marginTop: 3, fontSize: 12.5, fontWeight: 700, color: FAM.ink2 }}>{label}</div>
    {sub && <div style={{ marginTop: 2, fontSize: 11.5, color: FAM.muted, lineHeight: 1.45 }}>{sub}</div>}
  </div>
);

function fmtKm(km: number): string {
  if (km >= 1000) return `${Math.round(km).toLocaleString('en-KE')} km`;
  return `${km.toFixed(km < 10 ? 1 : 0)} km`;
}

function fmtWeek(d: Date): string {
  return d.toLocaleDateString('en-KE', { day: 'numeric', month: 'short' });
}

export default function FamilyAnalyticsPage({ orgCode, busId }: Props) {
  const { runs, schedule, loading, error } = useFamilyRuns(busId, orgCode);
  const { refuels } = useFamilyRefuels(orgCode);
  const [win, setWin] = useState<WindowKey>('4w');

  const scoped: FamilyRun[] = useMemo(() => {
    const days = WINDOWS.find((w) => w.key === win)?.days ?? 0;
    if (!days) return runs;
    const cut = new Date();
    cut.setDate(cut.getDate() - days);
    cut.setHours(0, 0, 0, 0);
    return runs.filter((r) => r.startedAt && r.startedAt >= cut);
  }, [runs, win]);

  const t = useMemo(() => totalsFor(scoped), [scoped]);

  /* Fuel counts ONLY what the parent agreed to. A pending declaration is a
     claim nobody has confirmed and a disputed one is actively questioned —
     either in a "you spent X" figure would be a number with nobody behind it. */
  const fuel = useMemo(() => {
    const days = WINDOWS.find((w) => w.key === win)?.days ?? 0;
    const cut = new Date();
    if (days) { cut.setDate(cut.getDate() - days); cut.setHours(0, 0, 0, 0); }
    const inWindow = refuels.filter((r) => !days || (r.declaredAt && r.declaredAt >= cut));
    return {
      spend: confirmedSpend(inWindow),
      awaiting: inWindow.filter(needsParent).length,
      counted: inWindow.filter((r) => !r.voided && r.status === 'confirmed').length,
    };
  }, [refuels, win]);

  /* Punctuality only counts runs we can actually judge — one with no scheduled
     time is not "on time" and not "late", it is unmeasurable, and folding it
     into either bucket would invent a statistic. */
  const punctual = useMemo(() => {
    let judged = 0; let onTime = 0; let lateSum = 0; let lateCount = 0;
    for (const r of scoped) {
      const late = minutesLate(r, schedule);
      if (late == null) continue;
      judged += 1;
      if (late <= ON_TIME_GRACE_MIN) onTime += 1;
      else { lateSum += late; lateCount += 1; }
    }
    return { judged, onTime, avgLate: lateCount ? Math.round(lateSum / lateCount) : 0, lateCount };
  }, [scoped, schedule]);

  const weeks = useMemo(() => byWeek(scoped).slice(0, 12).reverse(), [scoped]);

  const chartData = useMemo(() => weeks.map((w) => {
    const wt = totalsFor(w.runs);
    return {
      week: fmtWeek(w.weekStart),
      km: Number(wt.km.toFixed(1)),
      runs: wt.count,
      /* Weeks where every run lost its distance must not draw as a real zero —
         the bar is flagged and rendered hollow with a note instead. */
      unrecorded: wt.kmMissing > 0 && wt.km === 0,
    };
  }), [weeks]);

  const avgDuration = t.minutesCount ? Math.round(t.minutes / t.minutesCount) : null;

  if (loading) {
    return <Panel><p style={{ margin: 0, color: FAM.ink2, fontSize: 14.5 }}>Reading your runs…</p></Panel>;
  }

  if (error) {
    return (
      <Panel>
        <h2 style={{ fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: 28, margin: 0 }}>Analytics</h2>
        <p style={{ margin: '10px 0 0', fontSize: 14.5, color: FAM.ink2, lineHeight: 1.6 }}>
          We could not read your runs just now. This is us, not your household — refresh in a moment.
        </p>
      </Panel>
    );
  }

  return (
    <div style={FAMILY_PAGE_WRAP}>
      <Panel>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 260px', minWidth: 0 }}>
            <h2 style={{ fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: 30, letterSpacing: '-.01em', margin: 0 }}>
              Your school run
            </h2>
            <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: 1.6, color: FAM.ink2, maxWidth: 520 }}>
              Measured from the runs themselves — not from anything anyone typed in.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                type="button"
                onClick={() => setWin(w.key)}
                style={{
                  padding: '7px 13px', borderRadius: 999, cursor: 'pointer', font: 'inherit',
                  fontSize: 13, fontWeight: 700,
                  border: `1px solid ${win === w.key ? FAM.accent : FAM.line}`,
                  background: win === w.key ? FAM.accent : '#fff',
                  color: win === w.key ? '#fff' : FAM.ink2,
                }}
              >{w.label}</button>
            ))}
          </div>
        </div>

        {t.count === 0 ? (
          <div style={{
            marginTop: 20, padding: '18px 20px', borderRadius: 14,
            background: FAM.tint, border: `1px solid ${FAM.tint2}`,
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: FAM.ink }}>No runs recorded yet</div>
            <p style={{ margin: '8px 0 0', fontSize: 14, color: FAM.ink2, lineHeight: 1.6 }}>
              Once a school run is started from the Trasse app, it lands here — how long it took, how
              far it went, and who drove. Nothing is missing; there is simply nothing yet.
            </p>
          </div>
        ) : (
          <>
            <div style={{
              marginTop: 20, display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12,
            }}>
              <Stat
                value={String(t.count)}
                label={t.count === 1 ? 'run' : 'runs'}
                sub={t.completed < t.count ? `${t.count - t.completed} did not finish cleanly` : 'all finished'}
              />
              <Stat
                value={fmtKm(t.km)}
                label="driven"
                sub={t.kmMissing ? `${t.kmMissing} run${t.kmMissing === 1 ? '' : 's'} recorded no distance` : undefined}
              />
              <Stat
                value={avgDuration != null ? `${avgDuration} min` : '—'}
                label="typical run"
                sub={avgDuration != null ? `across ${t.minutesCount} timed run${t.minutesCount === 1 ? '' : 's'}` : 'no run has been timed yet'}
              />
              <Stat
                value={fuel.counted ? `KES ${Math.round(fuel.spend).toLocaleString('en-KE')}` : '—'}
                label="fuel you agreed to"
                sub={fuel.awaiting
                  ? `${fuel.awaiting} declaration${fuel.awaiting === 1 ? '' : 's'} still waiting on you`
                  : fuel.counted
                    ? `across ${fuel.counted} refuel${fuel.counted === 1 ? '' : 's'}`
                    : 'no refuel has been declared yet'}
              />
              <Stat
                value={punctual.judged ? `${Math.round((punctual.onTime / punctual.judged) * 100)}%` : '—'}
                label="started on time"
                sub={punctual.judged
                  ? `within ${ON_TIME_GRACE_MIN} min of plan${punctual.lateCount ? ` · late ones by ${punctual.avgLate} min` : ''}`
                  : 'no run had a scheduled time to compare against'}
              />
            </div>

            {/* who drove */}
            {(t.parentDrove > 0 || t.driverDrove > 0) && (
              <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {t.parentDrove > 0 && (
                  <span style={{
                    padding: '7px 13px', borderRadius: 999, background: FAM.posTint, color: FAM.pos,
                    fontSize: 13, fontWeight: 700,
                  }}>You drove {t.parentDrove}</span>
                )}
                {t.driverDrove > 0 && (
                  <span style={{
                    padding: '7px 13px', borderRadius: 999, background: FAM.tint2, color: FAM.accent,
                    fontSize: 13, fontWeight: 700,
                  }}>Your driver drove {t.driverDrove}</span>
                )}
                {t.count > t.parentDrove + t.driverDrove && (
                  <span style={{
                    padding: '7px 13px', borderRadius: 999, background: '#EFEDE7', color: FAM.muted,
                    fontSize: 13, fontWeight: 600,
                  }}>
                    {t.count - t.parentDrove - t.driverDrove} not attributed
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </Panel>

      {/* ── the weeks ─────────────────────────────────────────────────── */}
      {chartData.length > 0 && (
        <Panel>
          <h3 style={{ margin: 0, fontSize: 16.5, fontWeight: 800, color: FAM.ink }}>Distance by week</h3>
          <p style={{ margin: '6px 0 0', fontSize: 13.5, color: FAM.ink2 }}>
            Week beginning. A hollow bar is a week whose runs recorded no distance — not a week
            nobody drove.
          </p>
          <div style={{ width: '100%', height: 240, marginTop: 16, overflowX: 'auto' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={FAM.line} vertical={false} />
                <XAxis dataKey="week" tick={{ fontSize: 11.5, fill: FAM.ink3 }} tickLine={false} axisLine={{ stroke: FAM.line }} />
                <YAxis tick={{ fontSize: 11.5, fill: FAM.ink3 }} tickLine={false} axisLine={false} />
                <Tooltip
                  cursor={{ fill: FAM.tint }}
                  contentStyle={{ borderRadius: 12, border: `1px solid ${FAM.line}`, fontSize: 13 }}
                  formatter={(v: number, _n, p) => [
                    (p?.payload as { unrecorded?: boolean })?.unrecorded ? 'not recorded' : `${v} km`,
                    'Distance',
                  ]}
                />
                <Bar dataKey="km" radius={[7, 7, 0, 0]} maxBarSize={44}>
                  {chartData.map((d, i) => (
                    <Cell
                      key={i}
                      fill={d.unrecorded ? 'transparent' : FAM.accent}
                      stroke={d.unrecorded ? FAM.accentPale : undefined}
                      strokeDasharray={d.unrecorded ? '4 3' : undefined}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      )}

      {/* ── honest absences ───────────────────────────────────────────── */}
      <Panel>
        <span style={{
          display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 10px', borderRadius: 999,
          background: FAM.amberTint, color: FAM.amber, fontSize: 11, fontWeight: 800, letterSpacing: '.06em',
        }}>NOT MEASURED YET</span>
        <h3 style={{ margin: '12px 0 0', fontSize: 16.5, fontWeight: 800, color: FAM.ink }}>
          What Trasse cannot tell you yet
        </h3>
        <p style={{ margin: '8px 0 0', fontSize: 14, lineHeight: 1.6, color: FAM.ink2, maxWidth: 560 }}>
          These are absent rather than empty — the app is not recording them yet, so showing you a
          zero would be worse than showing you nothing.
        </p>
        <ul style={{ margin: '12px 0 0', paddingLeft: 20, fontSize: 14, lineHeight: 1.85, color: FAM.ink2 }}>
          <li><b>Driving quality</b> — harsh braking, speed, idling. Nothing records these today, for
            any Trasse vehicle, so there is no household figure to show you.</li>
          <li><b>Fuel efficiency</b> — km per litre. Deliberately absent rather than unbuilt: a refuel
            here is a declaration of what was spent, not a measurement of what went in, and Trasse
            does not build a consumption case against the one person who drives your child.</li>
        </ul>
      </Panel>
    </div>
  );
}
